import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { apiClient } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const emptyForm = { question: "", answer: "", category: "General", order: 0 };

export default function AdminFAQs() {
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchFaqs = () => apiClient.get("/admin/faqs").then((res) => setFaqs(res.data));
  useEffect(() => { fetchFaqs(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingId) await apiClient.put(`/admin/faqs/${editingId}`, form);
    else await apiClient.post("/admin/faqs", form);
    toast.success(`FAQ ${editingId ? "updated" : "created"}`);
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
    fetchFaqs();
  };

  const handleEdit = (f) => {
    setForm({ question: f.question, answer: f.answer, category: f.category, order: f.order });
    setEditingId(f.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    await apiClient.delete(`/admin/faqs/${id}`);
    toast.success("FAQ deleted");
    fetchFaqs();
  };

  return (
    <div data-testid="admin-faqs-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-semibold text-slate-900">FAQs</h1>
        <Button size="sm" className="rounded-md" onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }} data-testid="admin-new-faq-btn"><Plus className="h-4 w-4 mr-1.5" /> New FAQ</Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-md shadow-sm p-5 mb-6 space-y-3" data-testid="admin-faq-form">
          <div className="flex justify-between"><p className="text-sm font-semibold text-slate-900">{editingId ? "Edit FAQ" : "New FAQ"}</p><button type="button" onClick={() => setShowForm(false)}><X className="h-4 w-4 text-slate-400" /></button></div>
          <Input placeholder="Question" required value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-form-question" />
          <Textarea placeholder="Answer" required rows={3} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="faq-form-answer" />
          <Input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="faq-form-category" />
          <Button type="submit" size="sm" className="rounded-md" data-testid="faq-form-submit">Save FAQ</Button>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded-md shadow-sm divide-y divide-slate-100">
        {faqs.map((f) => (
          <div key={f.id} className="flex items-center justify-between px-4 py-3" data-testid={`admin-faq-row-${f.id}`}>
            <div>
              <p className="text-sm font-medium text-slate-900">{f.question}</p>
              <p className="text-xs text-slate-500">{f.category}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleEdit(f)} data-testid={`admin-edit-faq-${f.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" onClick={() => handleDelete(f.id)} data-testid={`admin-delete-faq-${f.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
            </div>
          </div>
        ))}
        {faqs.length === 0 && <p className="p-6 text-center text-slate-400 text-sm" data-testid="admin-no-faqs">No FAQs yet.</p>}
      </div>
    </div>
  );
}
